import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Calendar, Truck, CheckCircle, Package } from 'lucide-react';
import { Loader } from '@/components/ui/loader';
import { DashboardHeader } from '@/components/dashboard/DashboardHeader';
import { StatsGrid } from '@/components/dashboard/StatsGrid';
import { DashboardStat } from '@/types/dashboard';
import { QRScanner } from '@/components/driver/QRScanner';
import { PODUploadDialog } from '@/components/driver/PODUploadDialog';
import { ReportDelayDialog } from '@/components/driver/ReportDelayDialog';
import { GPSTrackingToggle } from '@/components/driver/GPSTrackingToggle';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDriverMissions, DriverMission } from '@/hooks/useDriverMissions';
import { DriverMissionCard } from '@/components/driver/DriverMissionCard';
import { useReportDelay } from '@/hooks/useReportDelay';
import { useDriverGeolocation } from '@/hooks/useDriverGeolocation';
import { useTranslation } from 'react-i18next';

const DriverMissions = () => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const [scannerOpen, setScannerOpen] = useState(false);
  const [podDialogOpen, setPodDialogOpen] = useState(false);
  const [delayDialogOpen, setDelayDialogOpen] = useState(false);
  const [selectedMission, setSelectedMission] = useState<DriverMission | null>(null);
  const [gpsTrackingEnabled, setGpsTrackingEnabled] = useState(() => {
    const saved = localStorage.getItem('driver_gps_tracking_enabled');
    return saved === 'true';
  });
  
  const {
    assignedMissions,
    activeMissions,
    completedMissions,
    isLoading,
    acceptMission,
    declineMission,
    isAccepting,
    isDeclining,
  } = useDriverMissions();

  const { reportDelay, isReporting } = useReportDelay();

  // Get the active mission's order ID for GPS tracking
  const activeMissionOrderId = activeMissions[0]?.order_id;
  
  // GPS tracking hook
  const { isTracking, lastPosition } = useDriverGeolocation({
    orderId: activeMissionOrderId || '',
    isActive: gpsTrackingEnabled && !!activeMissionOrderId,
  });

  // Persist GPS tracking state
  useEffect(() => {
    localStorage.setItem('driver_gps_tracking_enabled', String(gpsTrackingEnabled));
  }, [gpsTrackingEnabled]);

  const stats: DashboardStat[] = [
    {
      title: t('missions.assigned'),
      value: assignedMissions.length.toString(),
      icon: Calendar,
      color: 'text-[#FFBF07]',
      bgColor: 'bg-[#FFBF07]/10',
    },
    {
      title: t('missions.inProgress'),
      value: activeMissions.length.toString(),
      icon: Truck,
      color: 'text-[#0880AE]',
      bgColor: 'bg-[#0880AE]/10',
    },
    {
      title: t('missions.completed'),
      value: completedMissions.length.toString(),
      icon: CheckCircle,
      color: 'text-[#14A38B]',
      bgColor: 'bg-[#14A38B]/10',
    },
  ];

  const handleScan = (mission: DriverMission) => {
    setSelectedMission(mission);
    setScannerOpen(true);
  };

  const handleScanSuccess = () => {
    setScannerOpen(false);
    // Open POD upload dialog after successful scan
    setPodDialogOpen(true);
  };

  const handleOpenDocuments = (mission: DriverMission) => {
    setSelectedMission(mission);
    setPodDialogOpen(true);
  };

  const handleReportDelay = (mission: DriverMission) => {
    setSelectedMission(mission);
    setDelayDialogOpen(true);
  };

  const handleSubmitDelay = async (data: { estimated_delay_minutes: number; delay_reason?: string }) => {
    if (!selectedMission) return;

    await reportDelay({
      jobId: selectedMission.id,
      ...data,
    });

    setDelayDialogOpen(false);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader size="lg" />
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full pb-safe">
      <div className="px-4 md:px-6 lg:px-8 lg:max-w-7xl lg:mx-auto">
        <DashboardHeader 
          title={t('missions.myMissions')}
          userName={user?.name || user?.email}
        />

        <div className="mb-6 md:mb-8">
          <StatsGrid stats={stats} columns={3} />
        </div>

        {/* GPS Tracking Toggle - only show if there's an active mission */}
        {activeMissions.length > 0 && (
          <div className="mb-6 md:mb-8">
            <GPSTrackingToggle
              isEnabled={gpsTrackingEnabled}
              onToggle={setGpsTrackingEnabled}
              isTracking={isTracking}
              lastPosition={lastPosition}
            />
          </div>
        )}

        <Tabs defaultValue="assigned" className="w-full">
          <div className="overflow-x-auto -mx-4 px-4 md:mx-0 md:px-0 mb-6 md:mb-8">
            <TabsList className="inline-flex w-auto min-w-full md:grid md:w-full md:grid-cols-3 bg-[#EBF4F8] p-1.5 rounded-lg h-auto">
              <TabsTrigger 
                value="assigned"
                className="data-[state=active]:bg-white data-[state=active]:text-[#37699E] font-semibold text-base md:text-base py-3.5 md:py-3 whitespace-nowrap min-h-[44px] transition-all active:scale-95"
              >
                {t('missions.assigned')} {assignedMissions.length > 0 && `(${assignedMissions.length})`}
              </TabsTrigger>
              <TabsTrigger 
                value="active"
                className="data-[state=active]:bg-white data-[state=active]:text-[#37699E] font-semibold text-base md:text-base py-3.5 md:py-3 whitespace-nowrap min-h-[44px] transition-all active:scale-95"
              >
                {t('missions.active')} {activeMissions.length > 0 && `(${activeMissions.length})`}
              </TabsTrigger>
              <TabsTrigger 
                value="history"
                className="data-[state=active]:bg-white data-[state=active]:text-[#37699E] font-semibold text-base md:text-base py-3.5 md:py-3 whitespace-nowrap min-h-[44px] transition-all active:scale-95"
              >
                {t('missions.history')}
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="assigned" className="space-y-4 md:space-y-6">
            {assignedMissions.length === 0 ? (
              <div className="text-center py-12 md:py-16 bg-white rounded-xl border border-[#DBE2EA]">
                <Package className="mx-auto h-16 w-16 md:h-12 md:w-12 text-[#87ADD4] mb-4" />
                <p className="text-base md:text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                  {t('missions.empty.assigned')}
                </p>
              </div>
            ) : (
              assignedMissions.map((mission) => (
                <DriverMissionCard
                  key={mission.id}
                  mission={mission}
                  onAccept={acceptMission}
                  onDecline={declineMission}
                  isAccepting={isAccepting}
                  isDeclining={isDeclining}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="active" className="space-y-4 md:space-y-6">
            {activeMissions.length === 0 ? (
              <div className="text-center py-12 md:py-16 bg-white rounded-xl border border-[#DBE2EA]">
                <Truck className="mx-auto h-16 w-16 md:h-12 md:w-12 text-[#87ADD4] mb-4" />
                <p className="text-base md:text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                  {t('missions.empty.active')}
                </p>
              </div>
            ) : (
              activeMissions.map((mission) => (
                <DriverMissionCard
                  key={mission.id}
                  mission={mission}
                  onScan={handleScan}
                  onOpenDocuments={() => handleOpenDocuments(mission)}
                  onReportDelay={handleReportDelay}
                />
              ))
            )}
          </TabsContent>

          <TabsContent value="history" className="space-y-4 md:space-y-6">
            {completedMissions.length === 0 ? (
              <div className="text-center py-12 md:py-16 bg-white rounded-xl border border-[#DBE2EA]">
                <CheckCircle className="mx-auto h-16 w-16 md:h-12 md:w-12 text-[#87ADD4] mb-4" />
                <p className="text-base md:text-sm text-[#5A6B7A] font-['Funnel_Sans']">
                  {t('missions.empty.completed')}
                </p>
              </div>
            ) : (
              completedMissions.map((mission) => (
                <DriverMissionCard
                  key={mission.id}
                  mission={mission}
                  onOpenDocuments={() => handleOpenDocuments(mission)}
                />
              ))
            )}
          </TabsContent>
        </Tabs>
      </div>

      {selectedMission && (
        <>
          <QRScanner
            open={scannerOpen}
            onOpenChange={setScannerOpen}
          />
          <PODUploadDialog
            open={podDialogOpen}
            onOpenChange={setPodDialogOpen}
            orderId={selectedMission.order_id}
            orderNumber={selectedMission.orders.order_number}
          />
          <ReportDelayDialog
            open={delayDialogOpen}
            onOpenChange={setDelayDialogOpen}
            onSubmit={handleSubmitDelay}
            isSubmitting={isReporting}
          />
        </>
      )}
    </div>
  );
};

export default DriverMissions;
